"use client";
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useAuth as useClerkAuth, UserButton } from '@clerk/nextjs';
import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useAuthStore } from '@/store/auth-store';

interface NavItem { href: string; label: string; highlight?: boolean; }

const baseNav: NavItem[] = [
  { href: '/', label: 'Home' },
  { href: '/rotations', label: 'Rotations' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/testimonials', label: 'Testimonials' },
  { href: '/about', label: 'About' },
  { href: '/faq', label: 'FAQ' },
];

export default function MarketingHeader({ sticky = true }: { sticky?: boolean }) {
  const pathname = usePathname();
  const { isSignedIn, isLoaded } = useClerkAuth();
  const user = useAuthStore((state) => state.user);

  // Hydration fix
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Mobile nav panel — close automatically on route change so it doesn't
  // stay open after a link is clicked.
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Determine profile link based on user role
  const getProfileLink = () => {
    if (user?.role === 'admin') return '/admin/dashboard';
    if (user?.role === 'preceptor') return '/preceptor/dashboard';
    return '/student/profile';
  };

  // IMG context: the IMG landing + its own sub-pages and the IMG rotation search.
  const isImg = pathname === '/img' || pathname.startsWith('/img/') || pathname.startsWith('/preceptor/img');

  const imgNav: NavItem[] = [
    { href: '/img', label: 'Home' },
    { href: '/preceptor/img/search', label: 'Rotations' },
    { href: '/img/pricing', label: 'Pricing' },
    { href: '/img/faq', label: 'FAQ' },
    { href: '/np', label: 'For NP Students' },
  ];

  const nav: NavItem[] = isImg
    ? imgNav
    : [...baseNav, { href: '/become-preceptor', label: 'Become a Preceptor', highlight: true }];

  return (
    <header className={`${sticky ? 'sticky top-0' : 'relative'} z-50 bg-white/90 backdrop-blur border-b border-[var(--brand-border)]`}>
      <div className="max-w-7xl mx-auto h-20 flex items-center gap-6 px-6 md:px-10">
        <Link href={isImg ? '/img' : '/'} className="flex items-center gap-2.5" aria-label="ACE Rotations Home">
          <Image src="/images/Ace-Main-Logo.png" alt="American Clinical Experience" width={160} height={120} className="h-12 w-auto object-contain" priority />
          {isImg && (
            <span className="inline-flex items-center rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white whitespace-nowrap">
              IMG<span className="hidden sm:inline">&nbsp;Students</span>
            </span>
          )}
        </Link>
        <nav className="flex-1 hidden lg:flex items-center gap-8">
          {nav.map(item => {
            const active = pathname === item.href || pathname.startsWith(item.href + '/');
            const base = 'text-sm transition-colors';
            const normal = active ? 'text-[var(--brand-primary)] font-medium' : 'text-[var(--brand-muted)] hover:text-[var(--brand-primary)]';
            const highlight = 'px-3 py-1.5 rounded-md border border-[var(--brand-border)] bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-sm hover:shadow-md hover:from-blue-600 hover:to-cyan-600';
            return (
              <Link
                key={item.href}
                href={item.href}
                className={[ base, item.highlight ? highlight : normal ].join(' ')}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="ml-auto flex items-center gap-3">
          {isImg ? (
            <Link href="/contact" className="text-xs md:text-sm px-4 py-2 rounded-md bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-hover)] transition-colors">Contact Us</Link>
          ) : isMounted && isLoaded && isSignedIn ? (
            <>
              <Link
                href={getProfileLink()}
                className="text-xs md:text-sm px-3 py-2 rounded-md border border-[var(--brand-border)] hover:bg-[var(--brand-muted-bg)] transition-colors text-[var(--brand-muted)] hover:text-[var(--brand-primary)]"
              >
                {user?.role === 'admin' ? 'Dashboard' : 'My Profile'}
              </Link>
              <UserButton />
            </>
          ) : (
            <>
              <Link href="/student/np/login" className="text-xs md:text-sm px-3 py-2 rounded-md bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-hover)] transition-colors">Login</Link>
              <Link href="/student/np/signup" className="hidden md:inline text-xs md:text-sm px-3 py-2 rounded-md border border-[var(--brand-border)] hover:bg-[var(--brand-muted-bg)] transition-colors text-[var(--brand-muted)] hover:text-[var(--brand-primary)]">Signup</Link>
            </>
          )}
          {/* Mobile hamburger — visible below the desktop nav breakpoint */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-controls="marketing-mobile-nav"
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-[var(--brand-border)] text-[var(--brand-muted)] hover:bg-[var(--brand-muted-bg)] hover:text-[var(--brand-primary)] transition-colors"
          >
            {mobileOpen ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
          </button>
        </div>
      </div>

      {/* Mobile nav panel — slides under the header on lg-and-below */}
      <div
        id="marketing-mobile-nav"
        className={`lg:hidden border-t border-[var(--brand-border)] bg-white overflow-hidden transition-[max-height] duration-200 ease-out ${mobileOpen ? 'max-h-[28rem]' : 'max-h-0'}`}
      >
        <nav className="px-6 py-3 flex flex-col">
          {nav.map(item => {
            const active = pathname === item.href || pathname.startsWith(item.href + '/');
            const base = 'block py-2.5 text-sm transition-colors';
            const normal = active ? 'text-[var(--brand-primary)] font-medium' : 'text-[var(--brand-muted)] hover:text-[var(--brand-primary)]';
            const highlight = 'mt-2 inline-flex items-center justify-center self-start px-3 py-2 rounded-md bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-medium shadow-sm';
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className={[ base, item.highlight ? highlight : normal ].join(' ')}
              >
                {item.label}
              </Link>
            );
          })}
          {isImg ? (
            <Link
              href="/contact"
              onClick={() => setMobileOpen(false)}
              className="mt-2 inline-flex items-center justify-center self-start px-3 py-2 rounded-md bg-[var(--brand-primary)] text-white text-sm hover:bg-[var(--brand-primary-hover)] transition-colors"
            >
              Contact Us
            </Link>
          ) : (
            /* Signup is hidden from the top bar on mobile, but make it reachable here */
            isMounted && isLoaded && !isSignedIn && (
              <Link
                href="/student/np/signup"
                onClick={() => setMobileOpen(false)}
                className="mt-2 inline-flex items-center justify-center self-start px-3 py-2 rounded-md border border-[var(--brand-border)] text-sm text-[var(--brand-muted)] hover:text-[var(--brand-primary)] hover:bg-[var(--brand-muted-bg)] transition-colors"
              >
                Signup
              </Link>
            )
          )}
        </nav>
      </div>
    </header>
  );
}
